import { type WorkforceMapPoint } from '@libraries/typescript/api_client';

function milesBetween(
    origin: { readonly latitude: number; readonly longitude: number },
    destination: { readonly latitude: number; readonly longitude: number },
): number {
    const toRadians = Math.PI / 180;
    const deltaLatitude = (destination.latitude - origin.latitude) * toRadians;
    const deltaLongitude =
        (destination.longitude - origin.longitude) * toRadians;
    const haversineTerm =
        Math.sin(deltaLatitude / 2) ** 2 +
        Math.cos(origin.latitude * toRadians) *
            Math.cos(destination.latitude * toRadians) *
            Math.sin(deltaLongitude / 2) ** 2;
    return 3958.8 * 2 * Math.asin(Math.sqrt(haversineTerm));
}

export function nearbyCaregivers(
    points: ReadonlyArray<WorkforceMapPoint>,
    origin: { readonly latitude: number; readonly longitude: number },
    radiusMiles: number,
) {
    return points
        .map((point) => ({ point, miles: milesBetween(origin, point) }))
        .filter(({ miles }) => miles <= radiusMiles)
        .sort((a, b) => a.miles - b.miles);
}

export function radiusCirclePolygon(
    center: { readonly latitude: number; readonly longitude: number },
    radiusMiles: number,
): {
    readonly type: 'Polygon';
    readonly coordinates: Array<Array<[number, number]>>;
} {
    const radians = Math.PI / 180;
    const latitude = center.latitude * radians;
    const longitude = center.longitude * radians;
    const distance = radiusMiles / 3958.8;
    const ring: Array<[number, number]> = [];
    for (let step = 0; step < 64; step += 1) {
        const bearing = (step / 64) * 2 * Math.PI;
        const lat = Math.asin(
            Math.sin(latitude) * Math.cos(distance) +
                Math.cos(latitude) * Math.sin(distance) * Math.cos(bearing),
        );
        const lon =
            longitude +
            Math.atan2(
                Math.sin(bearing) * Math.sin(distance) * Math.cos(latitude),
                Math.cos(distance) - Math.sin(latitude) * Math.sin(lat),
            );
        ring.push([lon / radians, lat / radians]);
    }
    ring.push(ring[0]!);
    return { type: 'Polygon', coordinates: [ring] };
}
